/**
 * Administración de personas: cambiar el rol y retirar o devolver el acceso.
 *
 * Sin paginación ni búsqueda: el volumen previsto son decenas de personas, y una lista que cabe
 * en una página no necesita un mecanismo que haya que mantener.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROLES, type AdminUser, type Role, type SessionProfile } from '@clica/shared';
import { ApiRequestError, fetchAdminUsers, updateAdminUser } from '../api/client.js';
import { Aviso } from '../components/sistema/aviso.js';
import { Marco } from '../components/sistema/marco.js';
import { useAvisos } from '../components/sistema/avisos.js';
import { Boton } from '../components/sistema/boton.js';
import { DialogoConfirmacion } from '../components/sistema/dialogo-confirmacion.js';
import { Glifo } from '../components/sistema/glifo.js';

/**
 * Las claves de traducción que esta pantalla puede mostrar.
 *
 * Es una unión y no `string` a propósito: el tipado de i18next convierte en error de compilación
 * cualquier clave que no exista en el catálogo, y eso solo funciona si el tipo es concreto.
 */
type ClaveDeError =
  | 'admin.error.cannotChangeOwnRole'
  | 'admin.error.cannotBanSelf'
  | 'admin.error.cannotBanPlatformOwner'
  | 'admin.error.userNotFound'
  | 'auth.error.forbidden'
  | 'auth.error.generic';

function claveDeError(error: unknown): ClaveDeError {
  if (!(error instanceof ApiRequestError)) return 'auth.error.generic';
  switch (error.key) {
    case 'admin.error.cannot_change_own_role':
      return 'admin.error.cannotChangeOwnRole';
    case 'admin.error.cannot_ban_self':
      return 'admin.error.cannotBanSelf';
    case 'admin.error.cannot_ban_platform_owner':
      return 'admin.error.cannotBanPlatformOwner';
    case 'admin.error.user_not_found':
      return 'admin.error.userNotFound';
    case 'auth.error.forbidden':
      return 'auth.error.forbidden';
    default:
      return 'auth.error.generic';
  }
}

function Fila({
  user,
  isSelf,
  onChange,
  onError,
}: {
  user: AdminUser;
  isSelf: boolean;
  onChange: (updated: AdminUser) => void;
  onError: (error: unknown) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const avisos = useAvisos();
  const [saving, setSaving] = useState(false);
  const [porVetar, setPorVetar] = useState(false);
  const banned = user.bannedAt !== null;
  const name = user.displayName ?? user.email ?? t('admin.users.noName');

  async function apply(body: Parameters<typeof updateAdminUser>[1]): Promise<void> {
    setSaving(true);
    try {
      onChange(await updateAdminUser(user.id, body));
      // Las dos acciones de esta pantalla son las más consecuentes de la aplicación y no decían
      // absolutamente nada al terminar: ni que había funcionado, ni que no.
      avisos.correcto(t('admin.users.saved', { name }));
    } catch (error) {
      onError(error);
      avisos.fallo(t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <tr className="border-line border-b">
      <td className="p-3">
        {name}
        {user.isPlatformOwner && (
          <span className="text-text-dim ml-2 text-xs">({t('admin.users.platformOwner')})</span>
        )}
      </td>
      <td className="p-3 text-sm">{user.email}</td>
      <td className="p-3">
        <label className="sr-only" htmlFor={`rol-${user.id}`}>
          {t('admin.users.changeRole', { name })}
        </label>
        <select
          id={`rol-${user.id}`}
          value={user.role}
          // Nadie cambia su propio rol, tampoco quien es responsable de la plataforma: el
          // servidor lo rechaza igualmente, esto solo evita ofrecer una acción que va a fallar.
          disabled={saving || isSelf}
          onChange={(event) => void apply({ role: event.target.value as Role })}
          className="text-body rounded-chip border-line bg-surface min-h-11 border-2 px-3"
        >
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {t(`roles.${role}`)}
            </option>
          ))}
        </select>
      </td>
      <td className="p-3">
        {/* Estado por icono y palabra, nunca solo por color (principio IV). */}
        <span className="text-body flex items-center gap-1.5">
          <Glifo nombre={banned ? 'prohibido' : 'correcto'} size={18} />
          {banned ? t('admin.users.statusBanned') : t('admin.users.statusActive')}
        </span>
      </td>
      <td className="p-3">
        {!isSelf && !user.isPlatformOwner && (
          <>
            <Boton
              variante={banned ? 'secundaria' : 'destructiva'}
              cargando={saving}
              // Devolver el acceso no necesita confirmación; retirarlo sí: deja a alguien fuera
              // de la plataforma y era un clic directo, mientras borrar una pregunta preguntaba.
              onClick={() => (banned ? void apply({ banned: false }) : setPorVetar(true))}
            >
              {banned ? t('admin.users.unban') : t('admin.users.ban')}
            </Boton>

            <DialogoConfirmacion
              abierto={porVetar}
              destructivo
              titulo={t('admin.users.ban')}
              cuerpo={t('admin.users.banConfirm', { name })}
              confirmar={t('admin.users.ban')}
              onConfirmar={() => {
                setPorVetar(false);
                void apply({ banned: true });
              }}
              onCancelar={() => setPorVetar(false)}
            />
          </>
        )}
      </td>
    </tr>
  );
}

export function PantallaUsuarios({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  const [users, setUsers] = useState<readonly AdminUser[] | null>(null);
  const [errorKey, setErrorKey] = useState<ClaveDeError | null>(null);

  useEffect(() => {
    fetchAdminUsers()
      .then(setUsers)
      .catch((error: unknown) => setErrorKey(claveDeError(error)));
  }, []);

  return (
    <Marco
      profile={profile}
      migas={[{ texto: t('panel.admin.title'), a: '/' }, { texto: t('admin.users.title') }]}
    >
      <main id="contenido" className="mx-auto w-full max-w-4xl p-6">
        <h1 className="font-display text-h1 font-extrabold text-balance">
          {t('admin.users.title')}
        </h1>
        <p className="text-lead text-text-dim mt-2">{t('admin.users.lead')}</p>

        {errorKey !== null && (
          <Aviso tono="atencion" interrumpe className="mt-4">
            {t(errorKey)}
          </Aviso>
        )}

        {users === null && errorKey === null && (
          <p role="status" className="mt-6">
            {t('admin.users.loading')}
          </p>
        )}

        {users !== null && users.length === 0 && <p className="mt-6">{t('admin.users.empty')}</p>}

        {users !== null && users.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-left">
              <caption className="sr-only">{t('admin.users.title')}</caption>
              <thead>
                <tr className="border-line border-b">
                  <th scope="col" className="p-3">
                    {t('admin.users.columnName')}
                  </th>
                  <th scope="col" className="p-3">
                    {t('admin.users.columnEmail')}
                  </th>
                  <th scope="col" className="p-3">
                    {t('admin.users.columnRole')}
                  </th>
                  <th scope="col" className="p-3">
                    {t('admin.users.columnStatus')}
                  </th>
                  <th scope="col" className="p-3">
                    {t('admin.users.columnActions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <Fila
                    key={user.id}
                    user={user}
                    isSelf={user.id === profile.id}
                    onChange={(updated) =>
                      setUsers((current) =>
                        (current ?? []).map((u) => (u.id === updated.id ? updated : u)),
                      )
                    }
                    onError={(error) => setErrorKey(claveDeError(error))}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </Marco>
  );
}
